/**
 * Shared presentational card body for the workflow gallery (SA-27).
 * Used by WorkflowsPage (wrapped in a <Link>) and WorkflowPickerModal
 * (wrapped in the install <button>) — therefore renders NO interactive
 * elements itself (no nested links/buttons).
 *
 * Manifest-driven elements degrade per-field: no manifest → no audience
 * badge, no business-config chips, no self-test tile.
 */
import type { ReactNode } from 'react'
import { Badge } from '@/components/ui'
import type { WorkflowSummary } from '@/hooks/useWorkflows'
import type { IntakeRelation } from '@/utils/intakeRelation'

/** Business-language labels for `needs.business_config` keys (EN admin
 * UI copy; unknown keys render as-is). Registry:
 * backend/app/schemas/template_manifest.py BUSINESS_CONFIG_NEEDS. */
const BUSINESS_CONFIG_LABELS: Record<string, string> = {
  currency: 'Currency',
  country: 'Country',
  timezone: 'Timezone',
  bank_transfer_details: 'Bank transfer details',
}

function AudienceBadge({ audience }: { audience: string | null }) {
  if (audience === 'customer') return <Badge variant="info">Customer-facing</Badge>
  if (audience === 'internal') return <Badge variant="secondary">Internal</Badge>
  return null
}

/** E1 viewer (spec 2026-09-18 §5): the step-model kind gets its own chip
 * with the step count when the wire carries one. */
function KindChip({ workflow }: { workflow: WorkflowSummary }) {
  if (workflow.kind !== 'process') return null
  const n = workflow.step_count ?? null
  return (
    <Badge variant="outline">
      {n === null ? 'Process' : `Process · ${n} step${n === 1 ? '' : 's'}`}
    </Badge>
  )
}

/** E1 ownership lanes (spec 2026-09-18 §4): where a forked custom row came from. */
function ProvenanceLine({ workflow }: { workflow: WorkflowSummary }) {
  const f = workflow.forked_from
  if (!f) return null
  const when = f.forked_at ? new Date(f.forked_at).toLocaleDateString() : ''
  return (
    <p className="text-[11px] text-muted-foreground">
      Forked from {f.workflow_name} v{f.version_number}
      {when ? ` on ${when}` : ''}
    </p>
  )
}

export interface WorkflowGalleryCardProps {
  workflow: WorkflowSummary
  /** Optional extra block rendered at the bottom (e.g. "Used by" personas). */
  footer?: ReactNode
  /** SA-469 (spec D5): the agenda ↔ process relation — the page reads it off the library list. */
  relation?: IntakeRelation | null
}

export function WorkflowGalleryCard({ workflow, footer, relation = null }: WorkflowGalleryCardProps) {
  const needs = [
    ...workflow.required_capabilities,
    ...(workflow.manifest?.needs.business_config ?? []).map(
      (key) => BUSINESS_CONFIG_LABELS[key] ?? key,
    ),
  ]
  const scenarioCount = workflow.manifest?.scenario_count

  return (
    <div className="flex h-full flex-col gap-2 min-w-0">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">{workflow.name}</h3>
        <div className="flex shrink-0 items-center gap-1">
          <KindChip workflow={workflow} />
          <AudienceBadge audience={workflow.manifest?.audience ?? null} />
        </div>
      </div>
      {workflow.description && (
        <p className="text-xs text-muted-foreground">{workflow.description}</p>
      )}
      <ProvenanceLine workflow={workflow} />
      {relation?.kind === 'process' && (
        <p className="text-xs text-muted-foreground">Intake: {relation.agendaLabel}</p>
      )}
      {needs.length > 0 && (
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
            What it needs
          </p>
          <div className="flex flex-wrap gap-1">
            {needs.map((need) => (
              <Badge key={need} variant="outline" className="text-xs">
                {need}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {typeof scenarioCount === 'number' && (
        <p className="text-xs text-muted-foreground">
          Ships with {scenarioCount} self-test{scenarioCount === 1 ? '' : 's'}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-auto pt-1">
        <Badge variant="secondary">
          {workflow.kind === 'order'
            ? relation?.kind === 'agenda' ? `Intake agenda for ${relation.processName}` : 'Guided order flow'
            : workflow.kind === 'process'
              ? 'Step process'
              : 'Flow graph'}
        </Badge>
        {workflow.source === 'platform' && workflow.configured ? (
          <Badge className="bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-800">
            Customized
          </Badge>
        ) : workflow.source === 'platform' ? (
          <Badge>Product type</Badge>
        ) : (
          <Badge variant="warning">Custom</Badge>
        )}
        {/* E3 Edit: an author's unpublished draft is waiting on this row. */}
        {workflow.source === 'custom' && workflow.has_draft && <Badge variant="outline">Draft</Badge>}
        {workflow.config_conflict && <Badge variant="destructive">Needs attention</Badge>}
        {workflow.languages.map((lang) => (
          <Badge key={lang} variant="outline" className="text-xs">
            {lang}
          </Badge>
        ))}
        {workflow.current_version_number != null && (
          <span className="font-mono text-xs text-muted-foreground">
            v{workflow.current_version_number}
          </span>
        )}
      </div>
      {footer}
    </div>
  )
}
